/**
 * VAT (Value Added Tax) — 13% standard rate on Nepali invoices.
 *
 * @example
 * calculateVAT(1000);                        // { base: 1000, vat: 130, total: 1130, ... }
 * calculateVAT(1130, { inclusive: true });   // { base: 1000, vat: 130, total: 1130, ... }
 * extractVAT(1130);                          // 130
 * isAboveVATThreshold(60_00_000, "goods")    // true
 */

import { getRates } from "./data/index.js";
import type { FiscalYear, VatResult } from "./types.js";
import { ensureNonNegative, r2 } from "./util.js";

/** Options for `calculateVAT`. */
export interface VatOptions {
  /** Whether the input amount is VAT-inclusive (default `false`). */
  readonly inclusive?: boolean;
  /** Fiscal year (default current). */
  readonly fiscalYear?: FiscalYear;
  /** Override VAT rate (default = current FY rate, 13%). */
  readonly rate?: number;
}

/**
 * Compute VAT on a base amount. By default treats input as VAT-exclusive.
 * Pass `{ inclusive: true }` to back-out VAT from a tax-included total.
 *
 * @example
 * calculateVAT(1000)                         // exclusive: vat=130, total=1130
 * calculateVAT(1130, { inclusive: true })    // inclusive: base=1000, vat=130
 */
export function calculateVAT(amount: number, options: VatOptions = {}): VatResult {
  ensureNonNegative(amount, "calculateVAT");
  const rates = getRates(options.fiscalYear);
  const rate = options.rate ?? rates.vat.rate;
  if (options.inclusive) {
    const base = amount / (1 + rate);
    const vat = amount - base;
    return {
      base: r2(base),
      vat: r2(vat),
      total: r2(amount),
      rate,
      inclusive: true,
    };
  }
  const vat = amount * rate;
  return {
    base: r2(amount),
    vat: r2(vat),
    total: r2(amount + vat),
    rate,
    inclusive: false,
  };
}

/** Just return the VAT amount from an inclusive total. */
export function extractVAT(totalInclusive: number, fiscalYear?: FiscalYear): number {
  return calculateVAT(totalInclusive, { inclusive: true, fiscalYear }).vat;
}

/** Just return the VAT amount on top of an exclusive base. */
export function addVAT(base: number, fiscalYear?: FiscalYear): number {
  return calculateVAT(base, { inclusive: false, fiscalYear }).vat;
}

/** Get the current standard VAT rate (default 13%). */
export function getVATRate(fiscalYear?: FiscalYear): number {
  return getRates(fiscalYear).vat.rate;
}

/** Get the VAT-registration threshold for goods or services. */
export function getVATThreshold(
  type: "goods" | "service",
  fiscalYear?: FiscalYear,
): number {
  const rates = getRates(fiscalYear);
  return type === "goods" ? rates.vat.thresholdGoods : rates.vat.thresholdService;
}

/** True if a turnover triggers mandatory VAT registration. */
export function isAboveVATThreshold(
  turnover: number,
  type: "goods" | "service",
  fiscalYear?: FiscalYear,
): boolean {
  return turnover >= getVATThreshold(type, fiscalYear);
}
